import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-[100dvh] gradient-hero">
      <SEO
        title="Privacy Policy"
        description="How VIZ collects, uses, stores, and protects athlete account data, sport profile information, and visualization session history."
        path="/privacy"
        noIndex
      />
      <div className="container max-w-3xl px-6 py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <h1 className="font-display text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-10">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

        <div className="prose prose-invert max-w-none space-y-8 text-muted-foreground">
          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">1. Information We Collect</h2>
            <p>When you create an account, we collect your name, email address, and sport-related profile information (sport, experience level, goals, and mental challenges). We also collect usage data such as completed visualization sessions and performance ratings.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide and personalize AI-generated visualization scripts</li>
              <li>Track your mental training progress over time</li>
              <li>Manage your account and subscription</li>
              <li>Improve our services and develop new features</li>
              <li>Communicate important updates about your account</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">3. Data Storage & Security</h2>
            <p>Your data is stored securely using industry-standard encryption. Audio files and personal data are stored in secure cloud infrastructure. We implement appropriate technical and organizational measures to protect your information.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">4. Third-Party Services</h2>
            <p>We use the following third-party services:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong className="text-foreground">Stripe</strong> — for payment processing. Stripe's privacy policy governs payment data.</li>
              <li><strong className="text-foreground">ElevenLabs</strong> — for voice synthesis. Audio is generated using your questionnaire answers (no personally identifiable data is shared).</li>
              <li><strong className="text-foreground">AI Language Models</strong> — for script generation. Only your sport and session preferences are sent; no personal contact information.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">5. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access your personal data via your Profile page</li>
              <li>Update or correct your information at any time</li>
              <li>Delete your account and all associated data</li>
              <li>Export your data upon request</li>
              <li>Withdraw consent for data processing</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">6. Cookies</h2>
            <p>We use essential cookies to maintain your authentication session. We do not use advertising or third-party tracking cookies.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">7. Data Retention</h2>
            <p>We retain your data for as long as your account is active. When you delete your account, all personal data and generated content is permanently removed within 30 days.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">8. Contact</h2>
            <p>If you have any questions about this Privacy Policy, please contact us at <a href="mailto:support@viz.app" className="text-primary hover:underline">support@viz.app</a>.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
